import {
  AccountClusterCount,
  AcquireLockResult,
  BodyHashClusterItem,
  DecisionBody,
  DecisionSubmitResult,
  EventDetailResponse,
  HeartbeatResult,
  QueueFilters,
  QueueListItem,
  QueuePage,
  toUrlSearchParams,
} from '../models';
import {
  ContentModerationApiError,
  unwrapEnvelope,
  unwrapEnvelopeWithWarnings,
} from './envelope';
import { getApiBase, getHeaders, HeaderOptions } from './headers';

const BASE_PATH = '/api/contentmoderation';

function url(path: string): string {
  return `${getApiBase()}${BASE_PATH}${path}`;
}

export interface ApiCallOptions extends HeaderOptions {
  signal?: AbortSignal;
}

export async function fetchQueue(
  filters: QueueFilters,
  options: ApiCallOptions = {},
): Promise<QueuePage<QueueListItem>> {
  const params = toUrlSearchParams(filters);
  const response = await fetch(`${url('/queue')}?${params.toString()}`, {
    method: 'GET',
    headers: getHeaders(options),
    signal: options.signal,
  });
  return unwrapEnvelope<QueuePage<QueueListItem>>(response);
}

export async function fetchEventDetail(
  id: number,
  options: ApiCallOptions = {},
): Promise<EventDetailResponse> {
  const response = await fetch(url(`/event/${id}`), {
    method: 'GET',
    headers: getHeaders(options),
    signal: options.signal,
  });
  return unwrapEnvelope<EventDetailResponse>(response);
}

export async function acquireLock(
  id: number,
  options: ApiCallOptions = {},
): Promise<AcquireLockResult> {
  const response = await fetch(url(`/event/${id}/lock`), {
    method: 'POST',
    headers: getHeaders(options),
    signal: options.signal,
  });
  try {
    return await unwrapEnvelope<AcquireLockResult>(response);
  } catch (error) {
    if (error instanceof ContentModerationApiError && error.code === 'reviewLock.taken') {
      const raw = error.raw as { data?: AcquireLockResult } | undefined;
      const holderId = raw?.data?.holderReviewerAccountId ?? null;
      const lastHeartbeatAt = raw?.data?.lastHeartbeatAt ?? null;
      return {
        outcome: 'heldByAnotherReviewer',
        holderReviewerAccountId: holderId,
        lastHeartbeatAt,
      };
    }
    throw error;
  }
}

export async function heartbeatLock(
  id: number,
  options: ApiCallOptions = {},
): Promise<HeartbeatResult> {
  const response = await fetch(url(`/event/${id}/heartbeat`), {
    method: 'POST',
    headers: getHeaders(options),
    signal: options.signal,
  });
  try {
    await unwrapEnvelope<unknown>(response);
    return { ok: true };
  } catch (error) {
    if (error instanceof ContentModerationApiError) {
      return { ok: false, code: error.code };
    }
    throw error;
  }
}

export async function releaseLock(
  id: number,
  options: ApiCallOptions = {},
): Promise<void> {
  try {
    await fetch(url(`/event/${id}/lock`), {
      method: 'DELETE',
      headers: getHeaders(options),
      signal: options.signal,
    });
  } catch {
    // Fire-and-forget on screen close; network failures here are non-actionable.
  }
}

export async function submitDecision(
  id: number,
  body: DecisionBody,
  options: ApiCallOptions = {},
): Promise<DecisionSubmitResult> {
  const response = await fetch(url(`/event/${id}/decision`), {
    method: 'POST',
    headers: getHeaders(options),
    body: JSON.stringify(body),
    signal: options.signal,
  });
  return unwrapEnvelopeWithWarnings<DecisionSubmitResult['decision']>(response).then(
    ({ data, warnings }) => ({ decision: data, warnings }),
  );
}

export async function resetDecision(
  decisionId: number,
  reason: string,
  options: ApiCallOptions = {},
): Promise<void> {
  const response = await fetch(url(`/decision/${decisionId}/reset`), {
    method: 'POST',
    headers: getHeaders(options),
    body: JSON.stringify({ reason }),
    signal: options.signal,
  });
  await unwrapEnvelope<unknown>(response);
}

export async function fetchAccountCluster(
  accountId: number,
  options: ApiCallOptions = {},
): Promise<AccountClusterCount> {
  const response = await fetch(url(`/account/${accountId}/cluster`), {
    method: 'GET',
    headers: getHeaders(options),
    signal: options.signal,
  });
  return unwrapEnvelope<AccountClusterCount>(response);
}

export async function fetchBodyHashCluster(
  eventId: number,
  limit: number = 50,
  options: ApiCallOptions = {},
): Promise<BodyHashClusterItem[]> {
  const response = await fetch(url(`/body-hash-cluster/${eventId}?limit=${limit}`), {
    method: 'GET',
    headers: getHeaders(options),
    signal: options.signal,
  });
  return unwrapEnvelope<BodyHashClusterItem[]>(response);
}
